/* @ts-self-types="./photosmith_core.d.ts" */

/**
 * 就地调整 RGBA8 像素的亮度。
 *
 * `delta` 加到 R/G/B 三个通道上并饱和截断到 `[0, 255]`；**alpha 不变**——
 * 调亮度不应该改变透明度，这是与 Photoshop 一致的语义。
 *
 * 输入按非预乘 sRGB 解释：这是个直观的、面向 UI 滑块的算子，不做线性化。
 * 需要物理正确的曝光调整时应另写 `adjust_exposure_linear`，不要改这个函数的语义。
 *
 * # Panics
 *
 * 当 `pixels.len()` 不是 4 的倍数时 panic：那说明调用方传错了缓冲，
 * 静默处理会让错误在更深的地方以更难查的形式冒出来。
 * @param {Uint8Array} pixels
 * @param {number} delta
 */
export function adjust_brightness_rgba8(pixels, delta) {
    var ptr0 = passArray8ToWasm0(pixels, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    wasm.adjust_brightness_rgba8(ptr0, len0, pixels, delta);
}

/**
 * 只处理一个**行带**，供多 Worker 数据并行使用。
 *
 * 两趟卷积必须分开调用：纵向卷积需要读取行带之外 ±radius 行的**横向结果**，
 * 若每个 Worker 各自做完整两趟，带边界处会读到未模糊的原始像素，
 * 拼接后出现可见接缝——这是分块滤镜最典型的错误。
 *
 * 因此正确的并行流程是：
 * 1. 所有 Worker 并行做横向卷积（各自的行带，互不依赖）
 * 2. 同步
 * 3. 所有 Worker 并行做纵向卷积（读取完整的横向结果）
 * @param {Uint8Array} src
 * @param {Uint8Array} dst
 * @param {number} width
 * @param {number} y_start
 * @param {number} y_end
 * @param {number} radius
 */
export function blur_pass_horizontal(src, dst, width, y_start, y_end, radius) {
    const ptr0 = passArray8ToWasm0(src, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    var ptr1 = passArray8ToWasm0(dst, wasm.__wbindgen_malloc);
    var len1 = WASM_VECTOR_LEN;
    wasm.blur_pass_horizontal(ptr0, len0, ptr1, len1, dst, width, y_start, y_end, radius);
}

/**
 * @param {Uint8Array} src
 * @param {Uint8Array} dst
 * @param {number} width
 * @param {number} height
 * @param {number} y_start
 * @param {number} y_end
 * @param {number} radius
 */
export function blur_pass_vertical(src, dst, width, height, y_start, y_end, radius) {
    const ptr0 = passArray8ToWasm0(src, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    var ptr1 = passArray8ToWasm0(dst, wasm.__wbindgen_malloc);
    var len1 = WASM_VECTOR_LEN;
    wasm.blur_pass_vertical(ptr0, len0, ptr1, len1, dst, width, height, y_start, y_end, radius);
}

/**
 * 构建信息：版本 + 该 wasm 是否以 SIMD128 编译。
 *
 * 用途是让 JS 侧能验证"加载到的 wasm 确实是带 SIMD 的那一份"。仅靠浏览器
 * 特性探测不够——探测说引擎支持，不代表构建产物真的用上了。
 * @returns {string}
 */
export function build_info() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.build_info();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * 内容识别填充：`mask` 里 255 的像素被替换为从 `mask` 为 0 的区域采样的合理内容。
 *
 * 多分辨率金字塔从粗到细逐级传播/重建（DESIGN.md §2.11 的要求）；层数依图像
 * 尺寸自适应，最粗一层不小于 patch 尺寸。`seed` 决定随机初始化与随机搜索的
 * 结果——同输入同 seed 结果完全确定，换 seed 能得到不同的填充结果（PS 的
 * "生成"按钮换一种效果就是这个意思）。
 *
 * # Panics
 *
 * `pixels`/`mask` 长度与 `width*height` 不符时 panic。
 * @param {Uint8Array} pixels
 * @param {Uint8Array} mask
 * @param {number} width
 * @param {number} height
 * @param {number} seed
 * @returns {Uint8Array}
 */
export function content_aware_fill_rgba8(pixels, mask, width, height, seed) {
    const ptr0 = passArray8ToWasm0(pixels, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(mask, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.content_aware_fill_rgba8(ptr0, len0, ptr1, len1, width, height, seed);
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * 泛洪选择。
 *
 * * `pixels` —— RGBA8 非预乘 sRGB，行主序，长度必须是 `width*height*4`。
 * * `tolerance` —— **Oklab 距离**阈值。0 只选完全同色；黑↔白约 1.0。
 *   UI 的 0–255 容差滑块按 `t/255` 映射到这里。
 * * `contiguous` —— true 走**边缘感应**的 4 邻域泛洪（见下）；false 是
 *   全图阈值判定，不看连通性（PS「连续」勾选框语义）。
 *
 * 连续模式的边缘感应（相对纯颜色阈值泛洪，向 Photoshop 快速选择的手感
 * 靠近的关键一步）：每一步扩展除了原有的"候选像素跟种子的颜色距离在
 * 容差内"，还要求"候选像素跟发起扩展的这个已选中邻居之间的颜色距离"
 * 不超过一个更小的**局部跳变上限**——哪怕两片区域各自单独看都在种子
 * 容差内，中间隔着一条明显的硬边缘（跳变幅度超过局部上限）时也不会
 * 被越过。这是经典的"跟已选区域比较，不是只跟种子比较"边缘感应泛洪，
 * 不是 Photoshop 内部用的完整分割算法，但用现有的 wasm 泛洪架构就能做，
 * 不需要新算法储备/新依赖。见 `EDGE_STEP_FACTOR` 的注释说明这个上限
 * 怎么从 `tolerance` 派生。
 *
 * 返回 `width*height` 的蒙版（255/0）。
 *
 * # Panics
 *
 * 缓冲长度不符或种子越界时 panic——参数错误应当立刻炸在边界上。
 * @param {Uint8Array} pixels
 * @param {number} width
 * @param {number} height
 * @param {number} seed_x
 * @param {number} seed_y
 * @param {number} tolerance
 * @param {boolean} contiguous
 * @returns {Uint8Array}
 */
export function flood_select(pixels, width, height, seed_x, seed_y, tolerance, contiguous) {
    const ptr0 = passArray8ToWasm0(pixels, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.flood_select(ptr0, len0, width, height, seed_x, seed_y, tolerance, contiguous);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * 对整幅 RGBA8 图像做高斯模糊。
 *
 * # Panics
 * 缓冲长度与 `width * height * 4` 不符时 panic——静默处理会让错误在更深处冒出来。
 * @param {Uint8Array} pixels
 * @param {number} width
 * @param {number} height
 * @param {number} radius
 */
export function gaussian_blur_rgba8(pixels, width, height, radius) {
    var ptr0 = passArray8ToWasm0(pixels, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    wasm.gaussian_blur_rgba8(ptr0, len0, pixels, width, height, radius);
}

/**
 * 对一块矩形区域逐像素求仿射 MLS 位移场。区域左上角文档坐标为
 * `(origin_x, origin_y)`，尺寸 `w×h`；`pins_from_*`/`pins_to_*` 已经做过
 * 角色互换（`from`=图钉当前位置，`to`=图钉放置时的原始位置）。
 *
 * 返回 dx/dy 交错的数组，长度 `w*h*2`（`[dx0,dy0,dx1,dy1,...]`），直接喂给
 * `resample::warp_field_bilinear_rgba8` 之前需要在 TS 侧拆成两个数组
 * （`engine/puppet/mls.ts` 负责）。
 *
 * # Panics
 * `pins_from_x`/`pins_from_y`/`pins_to_x`/`pins_to_y` 长度不一致。
 * @param {Float32Array} pins_from_x
 * @param {Float32Array} pins_from_y
 * @param {Float32Array} pins_to_x
 * @param {Float32Array} pins_to_y
 * @param {number} origin_x
 * @param {number} origin_y
 * @param {number} w
 * @param {number} h
 * @returns {Float32Array}
 */
export function mls_affine_field(pins_from_x, pins_from_y, pins_to_x, pins_to_y, origin_x, origin_y, w, h) {
    const ptr0 = passArrayF32ToWasm0(pins_from_x, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayF32ToWasm0(pins_from_y, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayF32ToWasm0(pins_to_x, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArrayF32ToWasm0(pins_to_y, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.mls_affine_field(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, origin_x, origin_y, w, h);
    var v5 = getArrayF32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v5;
}

/**
 * 两个 sRGB u8 颜色的 Oklab 距离。导出供 TS 侧测试与容差 UI 校准。
 * @param {number} r1
 * @param {number} g1
 * @param {number} b1
 * @param {number} r2
 * @param {number} g2
 * @param {number} b2
 * @returns {number}
 */
export function oklab_distance_srgb8(r1, g1, b1, r2, g2, b2) {
    const ret = wasm.oklab_distance_srgb8(r1, g1, b1, r2, g2, b2);
    return ret;
}

/**
 * 有多少个位置能当候选源 patch（整块 7×7 都在洞外）。
 *
 * TS 侧用它判断"这个选区能不能内容识别填充"——0 表示图里没有任何完整的
 * 已知 patch（洞几乎占满全图），此时不该调用 `content_aware_fill_rgba8`，
 * 它会原样返回输入（没有可抄的源，填不出东西不是 bug）。
 * @param {Uint8Array} mask
 * @param {number} width
 * @param {number} height
 * @returns {number}
 */
export function patch_match_valid_source_count(mask, width, height) {
    const ptr0 = passArray8ToWasm0(mask, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.patch_match_valid_source_count(ptr0, len0, width, height);
    return ret >>> 0;
}

/**
 * 把非预乘 RGBA8 转成预乘 RGBA8（就地）。
 *
 * 合成器在做混合前需要预乘形式；分离出来是因为它是热路径上最典型的
 * SIMD 受益者（每像素 3 次乘法 + 除法），后续会用它来量化 SIMD 加速比。
 *
 * 采用 `(c * a + 127) / 255` 的四舍五入近似，而不是 `c * a / 255`：
 * 后者系统性偏暗，往返转换若干次后可见。
 * @param {Uint8Array} pixels
 */
export function premultiply_alpha_rgba8(pixels) {
    var ptr0 = passArray8ToWasm0(pixels, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    wasm.premultiply_alpha_rgba8(ptr0, len0, pixels);
}

/**
 * 轴对齐 Lanczos-3 缩放。缩小 >2× 先盒式整数倍预降采样。
 *
 * # Panics
 * 尺寸为 0 或缓冲长度不符时 panic。
 * @param {Uint8Array} src
 * @param {number} sw
 * @param {number} sh
 * @param {number} dw
 * @param {number} dh
 * @returns {Uint8Array}
 */
export function resample_lanczos3_rgba8(src, sw, sh, dw, dh) {
    const ptr0 = passArray8ToWasm0(src, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.resample_lanczos3_rgba8(ptr0, len0, sw, sh, dw, dh);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * 对 `pixels`（RGBA8，`width×height`）算显著性蒙版：背景参考色取自边框
 * 一圈（不是全图均值）→ 逐像素颜色距离乘中心偏置 → Otsu 自适应阈值二值化
 * → 闭运算填洞/去毛刺 → 只保留最大连通块。输出尺寸与输入一致。全图颜色
 * 完全一致时（平均显著性为 0）返回全零蒙版，不误判成"全选"。
 *
 * # Panics
 * `pixels` 长度不等于 `width*height*4`。
 * @param {Uint8Array} pixels
 * @param {number} width
 * @param {number} height
 * @returns {Uint8Array}
 */
export function saliency_mask_rgba8(pixels, width, height) {
    const ptr0 = passArray8ToWasm0(pixels, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.saliency_mask_rgba8(ptr0, len0, width, height);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * 该 wasm 模块是否以 SIMD128 编译。
 * @returns {boolean}
 */
export function simd128_enabled() {
    const ret = wasm.simd128_enabled();
    return ret !== 0;
}

/**
 * 仿射逆映射 + 双线性（旋转/斜切的提交路径）。
 *
 * `inv` 是目标→源的仿射（a,b,c,d,e,f，列主 [a c e; b d f]，与 Canvas/CSS 一致）。
 * 源外采样为透明。
 * @param {Uint8Array} src
 * @param {number} sw
 * @param {number} sh
 * @param {number} dw
 * @param {number} dh
 * @param {Float32Array} inv
 * @returns {Uint8Array}
 */
export function transform_bilinear_rgba8(src, sw, sh, dw, dh, inv) {
    const ptr0 = passArray8ToWasm0(src, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayF32ToWasm0(inv, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.transform_bilinear_rgba8(ptr0, len0, sw, sh, dw, dh, ptr1, len1);
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * 位移场逆映射 + 双线性（液化提交/预览路径，ADR-0013）。
 *
 * `output(y,x) = bilinear(src, x - dx[y*w+x], y - dy[y*w+x])`——与
 * `transform_bilinear_rgba8` 的"目标→源"方向一致，只是每个目标像素有
 * 自己的一对位移量，不是共享一个仿射矩阵。`dx`/`dy` 长度必须恰为
 * `w*h`，与源同尺寸同网格（液化不改变图层外接矩形，源=目标同一张网格）。
 * 位移后落到源范围外的采样**钳到边缘**（见 `bilinear_sample_clamped`），
 * 不是返回透明——液化的位移累加是近似模型，拖动路径两端偶尔会让采样点
 * 越过图层边缘几像素，钳边（内容朝外拉伸）比抠出一块透明黑洞更接近真实
 * 图像编辑器的液化观感。
 *
 * # Panics
 * `src` 长度不等于 `w*h*4`，或 `dx`/`dy` 长度不等于 `w*h`。
 * @param {Uint8Array} src
 * @param {number} w
 * @param {number} h
 * @param {Float32Array} dx
 * @param {Float32Array} dy
 * @returns {Uint8Array}
 */
export function warp_field_bilinear_rgba8(src, w, h, dx, dy) {
    const ptr0 = passArray8ToWasm0(src, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayF32ToWasm0(dx, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayF32ToWasm0(dy, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.warp_field_bilinear_rgba8(ptr0, len0, w, h, ptr1, len1, ptr2, len2);
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg___wbindgen_copy_to_typed_array_c7f28e53671b41e8: function(arg0, arg1, arg2) {
            new Uint8Array(arg2.buffer, arg2.byteOffset, arg2.byteLength).set(getArrayU8FromWasm0(arg0, arg1));
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./photosmith_core_bg.js": import0,
    };
}

function getArrayF32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedFloat32ArrayMemory0 = null;
function getFloat32ArrayMemory0() {
    if (cachedFloat32ArrayMemory0 === null || cachedFloat32ArrayMemory0.byteLength === 0) {
        cachedFloat32ArrayMemory0 = new Float32Array(wasm.memory.buffer);
    }
    return cachedFloat32ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getFloat32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedFloat32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (!module.ok) {
            throw new Error(`failed to fetch Wasm: ${module.status} ${module.statusText} fetching '${module.url}'`);
        }

        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('photosmith_core_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
